import { useEffect, useState } from 'react';
import {
  Building2,
  Check,
  Download,
  Eye,
  EyeOff,
  Link2,
  Loader2,
  MapPin,
  Pencil,
  ReceiptText,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { AdminCustomerLocation } from '@/hooks/admin/useAdminCustomerDetail';
import {
  useCreateLocation,
  useImportSfLocation,
  useLinkSfCustomer,
  useSetBillingLocation,
  useSetLocationActive,
  useSfCustomerSearch,
  useSfLocations,
  useUpdateLocation,
  type LocationFields,
} from '@/hooks/admin/useAdminLocations';
import { useCustomerDetailContext } from '@/pages/admin/AdminCustomerDetail';

/**
 * Locations panel — the first sub-tab of the customer's Locations &
 * deliveries page (DeliveriesTab.tsx). Staff add/edit/hide of the customer's delivery locations,
 * plus a live "what does Service Fusion have" panel with one-click import.
 *
 * These rows are what the customer's ship-to picker offers at checkout and
 * what submit-order stamps on the SF job. Edits here are portal-local:
 * QuickBooks sub-customers refresh via the Enable button, and Service Fusion
 * records are managed in SF itself — the panel below reads SF, it never
 * writes to it.
 */
export function LocationsPanel() {
  const { customer, refetch } = useCustomerDetailContext();
  const [adding, setAdding] = useState(false);
  const create = useCreateLocation(customer.id);

  return (
    <div className="space-y-4">
      {/* Local locations */}
      <div className="rounded-xl border border-brix-border bg-brix-surface p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-brix-ink">
              Delivery locations
            </h2>
            <p className="text-xs leading-relaxed text-brix-muted mt-0.5 max-w-2xl">
              What the customer can pick as a ship-to when ordering. Changes here apply to the
              ordering portal only — QuickBooks refreshes via Enable, and Service Fusion is
              managed in SF (read-only panel below).
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
            <Plus size={13} /> Add location
          </Button>
        </div>

        {adding && (
          <LocationForm
            initial={{}}
            busy={create.isPending}
            error={create.error instanceof Error ? create.error.message : null}
            submitLabel="Create location"
            onCancel={() => setAdding(false)}
            onSubmit={(fields) =>
              create.mutate(fields, {
                onSuccess: () => {
                  setAdding(false);
                  refetch();
                },
              })
            }
          />
        )}

        {customer.locations.length === 0 ? (
          <div className="text-sm text-brix-muted italic">
            No locations on file — add one above, import from Service Fusion below, or re-run
            Enable to pull the QuickBooks address.
          </div>
        ) : (
          <div className="space-y-2">
            {customer.locations.map((loc) => (
              <LocationRow key={loc.id} loc={loc} customerId={customer.id} refetch={refetch} />
            ))}
          </div>
        )}
      </div>

      <SfPanel customerId={customer.id} refetch={refetch} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Local location row: display + inline edit + hide/show              */
/* ------------------------------------------------------------------ */

function LocationRow({
  loc,
  customerId,
  refetch,
}: {
  loc: AdminCustomerLocation;
  customerId: string;
  refetch: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateLocation(customerId);
  const setActive = useSetLocationActive(customerId);
  const setBilling = useSetBillingLocation(customerId);
  const [billingNotes, setBillingNotes] = useState<string[]>([]);

  const addr = [loc.address_line1, loc.address_line2, loc.city, loc.state, loc.zip]
    .filter(Boolean)
    .join(', ');
  const isPrimary = (loc.location_code ?? '').toUpperCase() === 'PRIMARY';

  if (editing) {
    return (
      <LocationForm
        initial={{
          display_name: loc.display_name,
          location_code: loc.location_code ?? '',
          address_line1: loc.address_line1 ?? '',
          address_line2: loc.address_line2 ?? '',
          city: loc.city ?? '',
          state: loc.state ?? '',
          zip: loc.zip ?? '',
          delivery_notes: loc.delivery_notes ?? '',
          phone: loc.phone ?? '',
        }}
        lockCode={isPrimary}
        busy={update.isPending}
        error={update.error instanceof Error ? update.error.message : null}
        submitLabel="Save changes"
        onCancel={() => setEditing(false)}
        onSubmit={(fields) =>
          update.mutate(
            { locationId: loc.id, fields },
            {
              onSuccess: () => {
                setEditing(false);
                refetch();
              },
            },
          )
        }
      />
    );
  }

  return (
    <div
      className={
        'rounded-lg border border-brix-border px-3.5 py-2.5 flex items-start justify-between gap-3 ' +
        (loc.active ? 'bg-brix-surface-alt/40' : 'bg-brix-surface-alt/40 opacity-60')
      }
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <MapPin size={14} className="text-brix-primary mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-brix-ink flex items-center gap-2 flex-wrap">
            {loc.display_name}
            {loc.location_code && (
              <span className="font-mono text-[10px] text-brix-muted border border-brix-border rounded px-1 py-px">
                {loc.location_code}
              </span>
            )}
            {loc.qbo_sub_customer_id != null && (
              <span
                className="font-mono text-[10px] text-brix-muted"
                title="Imported from this QuickBooks sub-customer — Enable refreshes it"
              >
                QBO #{loc.qbo_sub_customer_id}
              </span>
            )}
            {loc.is_billing && (
              <StatusBadge tone="info" size="sm">
                Bill-to
              </StatusBadge>
            )}
            {!loc.active && (
              <StatusBadge tone="warning" size="sm">
                Hidden
              </StatusBadge>
            )}
          </div>
          <div className="text-xs text-brix-muted">
            {addr || <span className="italic">no address on file</span>}
          </div>
          {loc.phone && (
            <div className="text-[11px] text-brix-muted mt-0.5">Store phone: {loc.phone}</div>
          )}
          {loc.delivery_notes && (
            <div className="text-[11px] text-brix-muted mt-0.5">Notes: {loc.delivery_notes}</div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {!loc.is_billing && (
          <Button
            variant="ghost"
            size="sm"
            disabled={setBilling.isPending || !loc.address_line1}
            title={
              loc.address_line1
                ? 'Make this the billing address — invoices are addressed here, and it is pushed to Service Fusion and QuickBooks'
                : 'Add a street address before this can be the bill-to'
            }
            onClick={() => {
              setBillingNotes([]);
              setBilling.mutate(
                { locationId: loc.id },
                {
                  onSuccess: (data) => {
                    setBillingNotes((data as { push_notes?: string[] })?.push_notes ?? []);
                    refetch();
                  },
                },
              );
            }}
          >
            {setBilling.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <ReceiptText size={12} />
            )}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)} title="Edit this location">
          <Pencil size={12} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={setActive.isPending}
          title={
            loc.active
              ? 'Hide from the ship-to picker (kept on record; orders already placed keep it)'
              : 'Show in the ship-to picker again'
          }
          onClick={() =>
            setActive.mutate(
              { locationId: loc.id, value: !loc.active },
              { onSuccess: () => refetch() },
            )
          }
        >
          {setActive.isPending ? (
            <Loader2 size={12} className="animate-spin" />
          ) : loc.active ? (
            <EyeOff size={12} />
          ) : (
            <Eye size={12} />
          )}
        </Button>
      </div>
      {billingNotes.length > 0 && (
        <div className="basis-full mt-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 space-y-1">
          <p className="text-[11px] font-semibold text-amber-700">
            Set here. Not everything reached the other systems:
          </p>
          {billingNotes.map((n) => (
            <p key={n} className="text-[11px] text-amber-700">
              · {n}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shared create/edit form                                            */
/* ------------------------------------------------------------------ */

function LocationForm({
  initial,
  lockCode = false,
  busy,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: LocationFields;
  lockCode?: boolean;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (fields: LocationFields) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState<LocationFields>({
    display_name: initial.display_name ?? '',
    location_code: initial.location_code ?? '',
    address_line1: initial.address_line1 ?? '',
    address_line2: initial.address_line2 ?? '',
    city: initial.city ?? '',
    state: initial.state ?? '',
    zip: initial.zip ?? '',
    delivery_notes: initial.delivery_notes ?? '',
    phone: initial.phone ?? '',
  });
  const set = (key: keyof LocationFields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <form
      className="rounded-lg border border-brix-primary/30 bg-brix-primary-tint/30 px-3.5 py-3 space-y-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!f.display_name?.trim()) return;
        onSubmit(f);
      }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <Field label="Location name *" className="sm:col-span-2">
          <Input value={f.display_name} onChange={set('display_name')} placeholder="Store name / site" />
        </Field>
        <Field label="Code">
          <Input
            value={f.location_code}
            onChange={set('location_code')}
            placeholder="auto"
            disabled={lockCode}
            title={lockCode ? "The primary location's code stays PRIMARY" : undefined}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Field label="Street">
          <Input value={f.address_line1} onChange={set('address_line1')} placeholder="Street address" />
        </Field>
        <Field label="Unit / suite">
          <Input value={f.address_line2} onChange={set('address_line2')} placeholder="Suite 200" />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        <Field label="City">
          <Input value={f.city} onChange={set('city')} />
        </Field>
        <Field label="State">
          <Input value={f.state} onChange={set('state')} maxLength={2} placeholder="CA" />
        </Field>
        <Field label="ZIP">
          <Input value={f.zip} onChange={set('zip')} maxLength={10} />
        </Field>
      </div>
      <Field label="Delivery notes (gate code, dock hours, …)">
        <Input value={f.delivery_notes} onChange={set('delivery_notes')} placeholder="Gate 4412 · deliver before 10am" />
        <Input value={f.phone} onChange={set('phone')} placeholder="Store phone — (555) 123-4567" />
      </Field>

      {error && <div className="text-xs text-brix-warning">{error}</div>}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={busy || !f.display_name?.trim()}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {submitLabel}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <X size={12} /> Cancel
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={'block ' + (className ?? '')}>
      <span className="block text-[11px] font-medium text-brix-muted mb-1">{label}</span>
      {children}
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Service Fusion panel                                               */
/* ------------------------------------------------------------------ */

function SfPanel({ customerId, refetch }: { customerId: string; refetch: () => void }) {
  const [checked, setChecked] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const sf = useSfLocations(customerId, checked);
  const importSf = useImportSfLocation(customerId);
  const [importingKey, setImportingKey] = useState<string | null>(null);

  const unresolved = checked && !!sf.data && sf.data.sf_customer_id == null;
  const showLinkSearch = unresolved || relinking;

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-brix-ink flex items-center gap-2">
            <Building2 size={14} className="text-brix-primary" />
            Service Fusion locations
          </h2>
          <p className="text-xs leading-relaxed text-brix-muted mt-0.5 max-w-2xl">
            Live read of the SF customer&apos;s service locations — nicknames, unit numbers, and
            gate info that never made it into the portal. Import copies one into the list above;
            nothing is written back to Service Fusion.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={sf.isFetching}
          onClick={() => (checked ? void sf.refetch() : setChecked(true))}
        >
          {sf.isFetching ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <RefreshCw size={13} />
          )}
          {checked ? 'Refresh from SF' : 'Check Service Fusion'}
        </Button>
      </div>

      {checked && sf.isLoading && (
        <div className="text-sm text-brix-muted">Reading Service Fusion…</div>
      )}
      {checked && sf.error instanceof Error && (
        <div className="text-xs text-brix-warning">Couldn&apos;t reach Service Fusion: {sf.error.message}</div>
      )}
      {checked && sf.data && (
        <>
          {sf.data.error && <div className="text-xs text-brix-warning">{sf.data.error}</div>}

          {/* SF customer link state. An unlinked customer can't submit orders
              when the QBO name isn't an exact SF customer name (SF 422s the
              job), so the link search shows automatically when unresolved. */}
          {sf.data.sf_customer_id != null && (
            <div className="flex items-center gap-2 text-[11px] text-brix-muted">
              <Link2 size={12} className="text-brix-primary" />
              Linked to Service Fusion customer{' '}
              <span className="font-mono">#{sf.data.sf_customer_id}</span>
              <button
                type="button"
                className="text-brix-primary hover:underline"
                onClick={() => setRelinking((v) => !v)}
              >
                {relinking ? 'Cancel' : 'Change link'}
              </button>
            </div>
          )}
          {showLinkSearch && (
            <SfLinkSearch
              customerId={customerId}
              onLinked={() => {
                setRelinking(false);
                refetch();
              }}
            />
          )}
          {(sf.data.locations ?? []).length === 0 && !sf.data.error && (
            <div className="text-sm text-brix-muted italic">
              Service Fusion has no locations on this customer.
            </div>
          )}
          <div className="space-y-2">
            {(sf.data.locations ?? []).map((loc, i) => {
              const key = String(loc.id ?? `${loc.street_1 ?? ''}-${i}`);
              const addr = [loc.street_1, loc.street_2, loc.city, loc.state_prov, loc.postal_code]
                .filter(Boolean)
                .join(', ');
              return (
                <div
                  key={key}
                  className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3.5 py-2.5 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-brix-ink flex items-center gap-2 flex-wrap">
                      {loc.nickname || loc.street_1 || 'Unnamed location'}
                      {loc.is_primary && (
                        <span className="text-[10px] text-brix-muted border border-brix-border rounded px-1 py-px">
                          SF primary
                        </span>
                      )}
                      {loc.is_bill_to && (
                        <span className="text-[10px] text-brix-muted border border-brix-border rounded px-1 py-px">
                          bill-to
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-brix-muted">{addr || <span className="italic">no address</span>}</div>
                    {(loc.is_gated || loc.gate_instructions) && (
                      <div className="text-[11px] text-brix-muted mt-0.5">
                        {loc.is_gated ? 'Gated' : ''}
                        {loc.is_gated && loc.gate_instructions ? ' · ' : ''}
                        {loc.gate_instructions ?? ''}
                      </div>
                    )}
                  </div>
                  {loc.on_file ? (
                    <StatusBadge tone="success" size="sm" icon={<Check size={11} />}>
                      On file
                    </StatusBadge>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={importSf.isPending}
                      onClick={() => {
                        setImportingKey(key);
                        importSf.mutate(loc, {
                          onSuccess: () => refetch(),
                          onSettled: () => setImportingKey(null),
                        });
                      }}
                    >
                      {importSf.isPending && importingKey === key ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Download size={12} />
                      )}
                      Import
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SF customer link search — fixes the submit-order 422               */
/* "Customer Name can not be found" for unlinked customers.           */
/* ------------------------------------------------------------------ */

function SfLinkSearch({
  customerId,
  onLinked,
}: {
  customerId: string;
  onLinked: () => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const search = useSfCustomerSearch(customerId, debounced);
  const link = useLinkSfCustomer(customerId);
  const [linkingId, setLinkingId] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 400);
    return () => clearTimeout(t);
  }, [term]);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 px-3.5 py-3 space-y-2.5">
      <div className="text-xs text-brix-ink">
        <span className="font-semibold">Link this account to its Service Fusion customer.</span>{' '}
        Orders can&apos;t reach Service Fusion until the account is linked — search SF by name
        below and pick the right record.
      </div>
      <div className="relative max-w-sm">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brix-muted" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search Service Fusion customers…"
          className="pl-8"
        />
      </div>

      {search.isFetching && <div className="text-xs text-brix-muted">Searching Service Fusion…</div>}
      {search.error instanceof Error && (
        <div className="text-xs text-brix-warning">{search.error.message}</div>
      )}
      {link.error instanceof Error && (
        <div className="text-xs text-brix-warning">{link.error.message}</div>
      )}
      {search.data && search.data.length === 0 && (
        <div className="text-xs text-brix-muted italic">
          No Service Fusion customers match “{debounced}”. If the customer doesn&apos;t exist in
          SF yet, create them there first.
        </div>
      )}
      {search.data && search.data.length > 0 && (
        <div className="space-y-1.5">
          {search.data.map((c) => (
            <div
              key={c.id}
              className="rounded-md border border-brix-border bg-brix-surface px-3 py-2 flex items-center justify-between gap-3"
            >
              <div className="min-w-0 text-sm text-brix-ink flex items-center gap-2 flex-wrap">
                <span className="truncate">{c.customer_name ?? `SF #${c.id}`}</span>
                <span className="font-mono text-[10px] text-brix-muted">#{c.id}</span>
                {c.qbo_id != null && (
                  <span className="font-mono text-[10px] text-brix-muted">QBO {c.qbo_id}</span>
                )}
                {c.qbo_match && (
                  <StatusBadge tone="success" size="sm">
                    QBO match
                  </StatusBadge>
                )}
                {c.linked && (
                  <StatusBadge tone="info" size="sm">
                    Currently linked
                  </StatusBadge>
                )}
              </div>
              {!c.linked && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={link.isPending}
                  onClick={() => {
                    setLinkingId(c.id);
                    link.mutate(c.id, {
                      onSuccess: () => onLinked(),
                      onSettled: () => setLinkingId(null),
                    });
                  }}
                >
                  {link.isPending && linkingId === c.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Link2 size={12} />
                  )}
                  Link
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
